"use client";

import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import type { AppLocale } from "@/core/i18n/config";
import { localizeHref } from "@/core/i18n/routing";
import { AssessmentFeaturedCard } from "@/features/assessment/presentation/components/assessment-featured-card";
import type { Assessment } from "@/features/assessment/types";

interface HomeFeaturedAssessmentsSectionProps {
  locale: AppLocale;
  assessments: Assessment[];
}

export function HomeFeaturedAssessmentsSection({
  locale,
  assessments,
}: HomeFeaturedAssessmentsSectionProps) {
  const isHi = locale === "hi";

  if (assessments.length === 0) return null;

  return (
    <section className="relative w-full overflow-hidden bg-white py-20 sm:py-28">
      <div className="mx-auto mb-12 max-w-7xl px-6 lg:px-8">
        <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55 }}
              className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-1.5"
            >
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
              <span className="text-xs font-semibold tracking-wider text-primary uppercase">
                {isHi ? "फीचर्ड असेसमेंट" : "Featured Assessment"}
              </span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.65, delay: 0.08 }}
              className="font-serif text-3xl font-light text-slate-900 sm:text-4xl lg:text-6xl"
            >
              {isHi ? (
                <>
                  खुद को समझने की
                  <br />
                  शुरुआत करें।
                </>
              ) : (
                <>
                  Start by understanding
                  <br />
                  where you are.
                </>
              )}
            </motion.h2>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, delay: 0.15 }}
          >
            <Link
              href={localizeHref(locale, "/assessment")}
              className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground shadow-md transition-all hover:scale-105 hover:bg-primary/90"
            >
              {isHi ? "सभी असेसमेंट देखें" : "Explore all assessments"}
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </motion.div>
        </div>
      </div>

      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-6 md:grid-cols-3 lg:px-8">
        {assessments.map((assessment, index) => (
          <AssessmentFeaturedCard
            key={assessment.id}
            assessment={assessment}
            locale={locale}
            index={index}
          />
        ))}
      </div>
    </section>
  );
}
